import { AlertCircle, ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import ScorecardCopyableError from "../Rows/ScorecardCopyableError";
import { getCriterionIcon } from "../../criterionCatalog";
import { SCORECARD_DETAIL_TITLE } from "../../constants";
import {
  describeCriterionResultContext,
  formatScorecardScore,
  formatScorecardVerdictLabel,
  getDisplayableEvaluatorEvidence,
  getStepEvaluatorErrorState,
  getVerdictBadgeClassName,
  isColumnAggregatePrimitive,
} from "../../utils";

import ScorecardRerunTextButton from "./ScorecardRerunTextButton";
import ScorecardScoreDeltaBadge from "./ScorecardScoreDeltaBadge";
import ScorecardScoreSummary from "./ScorecardScoreSummary";
import ScorecardSectionBlockHeader from "./ScorecardSectionBlockHeader";
import ScorecardSectionHeader from "./ScorecardSectionHeader";

import type { GetScorecardRowResponse, ScorecardStep } from "@/queries/smart-tables/scorecard";
import { ScorecardPrimitiveType, ScoreVerdict } from "@/queries/smart-tables/scorecard";
import type { SmartCell } from "@/queries/smart-tables/types";
import { cn } from "@/lib/utils";

interface ScorecardRowDetailViewProps {
  rowIndex: number;
  rowBreakdown?: GetScorecardRowResponse;
  steps: ScorecardStep[];
  getRowLabel?: (rowIndex: number) => string;
  getColumnLabel?: (columnId: string) => string;
  getRowCellValue?: (rowIndex: number, columnId: string) => string | null;
  focusedStepId?: string;
  getBackingCell?: (
    rowIndex: number,
    backingColumnId: string | null | undefined,
  ) => SmartCell | undefined;
  onRerunRow: (rowIndex: number) => void;
  isRerunning: boolean;
  isRerunDisabled: boolean;
  isReadOnly?: boolean;
}

const hasNumericScore = (score: unknown): score is number =>
  typeof score === "number" && Number.isFinite(score);

const formatComparedValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "Empty";
  }
  if (typeof value === "string") {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export const getCompareResultValues = (
  stepResult: GetScorecardRowResponse["step_results"][string] | undefined,
): { source: string; target: string } | null => {
  const rawValue = stepResult?.raw_value;
  if (!rawValue || typeof rawValue !== "object" || Array.isArray(rawValue)) {
    return null;
  }
  const result = rawValue as Record<string, unknown>;
  if (!("source" in result) || !("target" in result)) {
    return null;
  }
  return {
    source: formatComparedValue(result.source),
    target: formatComparedValue(result.target),
  };
};

export const getRowDetailSteps = ({
  steps,
  focusedStepId,
}: {
  steps: ScorecardStep[];
  focusedStepId?: string;
}): ScorecardStep[] => {
  if (!focusedStepId) {
    return steps;
  }

  return [...steps].sort((first, second) => {
    if (first.id === focusedStepId) {
      return -1;
    }
    if (second.id === focusedStepId) {
      return 1;
    }
    return 0;
  });
};

const ScorecardRowDetailView = ({
  rowIndex,
  rowBreakdown,
  steps,
  getRowLabel,
  getColumnLabel,
  getRowCellValue,
  focusedStepId,
  getBackingCell,
  onRerunRow,
  isRerunning,
  isRerunDisabled,
  isReadOnly = false,
}: ScorecardRowDetailViewProps) => {
  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);
  const label = getRowLabel?.(rowIndex) ?? `Row ${rowIndex + 1}`;

  // Keep every configured evaluator visible. When the row was opened from a specific evaluator,
  // move that evaluator first without turning the detail into a filtered view.
  const rowLevelSteps = useMemo(
    () => getRowDetailSteps({ steps, focusedStepId }),
    [focusedStepId, steps],
  );

  const rowDataEntries = useMemo(() => {
    const columnIds = new Set<string>();
    rowLevelSteps.forEach(step => {
      step.source_column_ids?.forEach(columnId => columnIds.add(columnId));
    });
    return Array.from(columnIds).map(columnId => ({
      columnId,
      label: getColumnLabel?.(columnId) ?? columnId,
      value: getRowCellValue?.(rowIndex, columnId),
    }));
  }, [getColumnLabel, getRowCellValue, rowIndex, rowLevelSteps]);

  const attentionCount = useMemo(
    () =>
      rowLevelSteps.reduce((count, step) => {
        const stepResult = rowBreakdown?.step_results?.[step.id];
        const { hasError } = getStepEvaluatorErrorState(
          stepResult,
          getBackingCell?.(rowIndex, step.backing_column_id),
        );
        return hasError || stepResult?.verdict === ScoreVerdict.FAIL ? count + 1 : count;
      }, 0),
    [getBackingCell, rowBreakdown, rowIndex, rowLevelSteps],
  );

  useEffect(() => {
    setExpandedStepId(focusedStepId ?? null);
  }, [focusedStepId, rowIndex]);

  useEffect(() => {
    if (expandedStepId && !rowLevelSteps.some(step => step.id === expandedStepId)) {
      setExpandedStepId(null);
    }
  }, [expandedStepId, rowLevelSteps]);

  return (
    <div className="space-y-4">
      <section className="space-y-2">
        <ScorecardSectionHeader
          label="Row"
          action={
            isReadOnly ? undefined : (
              <ScorecardRerunTextButton
                onClick={() => onRerunRow(rowIndex)}
                isRerunning={isRerunning}
                isDisabled={isRerunDisabled}
              />
            )
          }
        />
        <p className={SCORECARD_DETAIL_TITLE}>{label}</p>
        <ScorecardScoreSummary
          meta="row evaluator score"
          score={formatScorecardScore(rowBreakdown?.aggregate_score ?? null)}
          drift={rowBreakdown?.drift_summary?.drift}
          verdictLabel={formatScorecardVerdictLabel(rowBreakdown?.aggregate_verdict)}
          verdictClassName={getVerdictBadgeClassName(rowBreakdown?.aggregate_verdict)}
        />

        {rowBreakdown?.error_summary ? (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{rowBreakdown.error_summary}</span>
          </div>
        ) : null}
      </section>

      {rowDataEntries.length > 0 ? (
        <section className="space-y-2">
          <ScorecardSectionBlockHeader
            label="Inputs evaluated"
            description="These are the source column values the row evaluators used. They are not the scorecard result."
          />
          <div className="overflow-hidden rounded-lg border border-border/70">
            {rowDataEntries.map(entry => (
              <div
                key={entry.columnId}
                className="grid grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-3 border-b border-border/60 px-3 py-2.5 text-sm last:border-b-0"
              >
                <span className="truncate text-muted-foreground" title={entry.label}>
                  {entry.label}
                </span>
                <span
                  className="truncate font-medium text-foreground"
                  title={entry.value ?? undefined}
                >
                  {entry.value ?? "—"}
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="space-y-2">
        <ScorecardSectionHeader
          label="Evaluators"
          action={
            attentionCount > 0 ? (
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                {attentionCount} need{attentionCount === 1 ? "s" : ""} attention
              </span>
            ) : undefined
          }
        />
        <div className="rounded-lg border border-border/70 bg-background">
          {rowLevelSteps.map(step => {
            const stepResult = rowBreakdown?.step_results?.[step.id];
            const isExpanded = expandedStepId === step.id;
            const isFocused = step.id === focusedStepId;
            const isAggregateContribution =
              isColumnAggregatePrimitive(step.primitive_type) &&
              !hasNumericScore(stepResult?.score);
            const Icon = getCriterionIcon(step.primitive_type);
            const { hasError, errorDetail } = getStepEvaluatorErrorState(
              stepResult,
              getBackingCell?.(rowIndex, step.backing_column_id),
            );
            const isFailing = !hasError && stepResult?.verdict === ScoreVerdict.FAIL;
            const needsAttention = hasError || isFailing;
            const displayableEvidence = getDisplayableEvaluatorEvidence(stepResult?.evidence);
            const compareValues =
              step.primitive_type === ScorecardPrimitiveType.COMPARE
                ? getCompareResultValues(stepResult)
                : null;

            return (
              <div
                key={step.id}
                id={`scorecard-row-step-${step.id}`}
                className={cn(
                  "border-b border-border/60 last:border-b-0",
                  isFocused && !needsAttention && "bg-muted/10",
                  needsAttention && "bg-red-50/50",
                )}
              >
                <button
                  type="button"
                  onClick={() => setExpandedStepId(isExpanded ? null : step.id)}
                  className={cn(
                    "flex w-full items-start gap-3 px-3 py-3 text-left",
                    needsAttention ? "hover:bg-red-50" : "hover:bg-muted/20",
                  )}
                >
                  <Icon
                    className={cn(
                      "mt-1 h-4 w-4 shrink-0",
                      needsAttention ? "text-red-700" : "text-muted-foreground",
                    )}
                  />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <span className="line-clamp-2 min-w-0 break-words text-sm font-medium leading-5 text-foreground">
                        {step.title}
                      </span>
                      <div className="flex shrink-0 items-center gap-2">
                        {isFocused ? (
                          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                            Opened from
                          </span>
                        ) : null}
                        {needsAttention ? (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[11px] font-medium text-red-700">
                            <AlertCircle className="h-3 w-3" />
                            {hasError ? "Error" : "Fail"}
                          </span>
                        ) : null}
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {isAggregateContribution
                          ? "Included in aggregate result"
                          : `${formatScorecardScore(stepResult?.score ?? null)} evaluator score`}
                      </span>
                      <ScorecardScoreDeltaBadge drift={stepResult?.drift} />
                    </div>
                  </div>
                </button>
                {isExpanded ? (
                  <div className="space-y-2 px-3 pb-3 pl-14 text-xs text-muted-foreground">
                    {compareValues ? (
                      <div className="overflow-hidden rounded-md border border-border/60 bg-background">
                        {[
                          ["Source", compareValues.source],
                          ["Compared against", compareValues.target],
                        ].map(([valueLabel, comparedValue]) => (
                          <div
                            key={valueLabel}
                            className="grid grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)] gap-3 border-b border-border/50 px-2.5 py-2 last:border-b-0"
                          >
                            <span className="text-muted-foreground">{valueLabel}</span>
                            <span className="select-text whitespace-pre-wrap break-words text-right font-medium text-foreground">
                              {comparedValue}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {!hasError
                      ? (() => {
                          const context = describeCriterionResultContext(stepResult, step);
                          if (!context) {
                            return null;
                          }
                          return (
                            <div className="space-y-1">
                              <div className="flex items-start justify-between gap-2">
                                <span className="min-w-0 text-muted-foreground">
                                  {context.label}
                                </span>
                                <span className="min-w-0 truncate text-right font-medium text-foreground">
                                  {context.value}
                                </span>
                              </div>
                              {context.explanation ? (
                                <p className="select-text whitespace-pre-wrap break-words">
                                  {context.explanation}
                                </p>
                              ) : null}
                            </div>
                          );
                        })()
                      : null}
                    {hasError ? (
                      <ScorecardCopyableError
                        message={errorDetail ?? "Evaluator returned an error."}
                        compact
                      />
                    ) : null}
                    {!hasError && displayableEvidence ? (
                      <div>
                        <div className="font-medium text-foreground">Evidence</div>
                        <div className="select-text whitespace-pre-wrap break-words">
                          {displayableEvidence}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default ScorecardRowDetailView;
